// La ventana de "Conectar Mercado Libre": qué va a poder hacer Atlas y qué no, antes de abrir la
// autorización. La usan el chip de Mi marca (`MercadoLibreStatus`) y "Subir varios"
// (`AgregarVariosModal`), así las dos dicen lo mismo
// (ADR-2026-10-22-subir-varios-conecta-con-la-misma-explicacion).
import React from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { MercadoLibreIcon } from "./MercadoLibreIcon";

export const MercadoLibreConnectModal: React.FC<{
  open: boolean;
  /** Mercado Libre pidió volver a conectar: cambia el título. */
  reconectar?: boolean;
  conectando: boolean;
  error: string | null;
  onCancel: () => void;
  /** Tiene que abrir la ventana de Mercado Libre SINCRÓNICAMENTE (Safari). */
  onConnect: () => void;
  /** 80 en Mi marca; más arriba si la abre otro modal (Subir varios). */
  zIndex?: number;
}> = ({
  open,
  reconectar = false,
  conectando,
  error,
  onCancel,
  onConnect,
  zIndex = 80,
}) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4"
      style={{ zIndex }}
    >
      <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
            {reconectar ? "Reconectar Mercado Libre" : "Conectar Mercado Libre"}
          </h3>
          <button
            type="button"
            onClick={() => !conectando && onCancel()}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mb-6 space-y-3 text-[length:var(--text-body)] text-neutral-600">
          <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto">
            <MercadoLibreIcon className="h-5 w-5" />
          </div>
          <p>
            Atlas va a poder traer tus publicaciones como productos y subirles
            las fotos que generes, sin descargarlas ni volver a cargarlas a
            mano.
          </p>
          <p>
            Apenas conectes, traemos tus publicaciones activas y pausadas, y
            elegimos las mejores fotos de cada una. Podés seguir usando Atlas
            mientras tanto.
          </p>
          <p>
            No publica productos nuevos, no toca tus ventas ni tus preguntas, y
            no cambia nada sin que vos lo confirmes.
          </p>
          <p className="text-[13px] text-neutral-500">
            Tiene que conectarla el dueño de la cuenta (no un colaborador). Se
            abre una ventana de Mercado Libre para que lo autorices.
          </p>
          {error && <p className="text-[13px] text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => onCancel()}
            disabled={conectando}
            className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConnect}
            disabled={conectando}
            className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
          >
            {conectando && <Loader2 className="w-4 h-4 animate-spin" />}
            {conectando ? "Conectando…" : "Conectar"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
